import React, { useMemo } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Link,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import type { BillingRecord } from "@services/billing";

type SubscriptionCancelledDialogProps = {
  open: boolean;
  record: BillingRecord | null;
  onBackToBilling: () => void;
  onResubscribe: () => void;
};

function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = String(value).trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const slash = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(trimmed);
  if (slash) {
    return new Date(Number(slash[3]), Number(slash[1]) - 1, Number(slash[2]));
  }
  const d = new Date(trimmed);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatShortDate(value: string | null | undefined): string {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

export const SubscriptionCancelledDialog: React.FC<
  SubscriptionCancelledDialogProps
> = ({ open, record, onBackToBilling, onResubscribe }) => {
  const facilityName = record?.facilityName || "Facility";
  const periodEndRaw =
    record?.planEndDate || record?.planEndDateIso || record?.expiryDate;

  const accessUntil = formatShortDate(periodEndRaw);

  const removedOn = useMemo(() => {
    const end = parseDate(periodEndRaw);
    if (!end) return "—";
    return addDays(end, 1).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [periodEndRaw]);

  const archivedUntil = useMemo(() => {
    const end = parseDate(periodEndRaw);
    if (!end) return "—";
    return addMonths(end, 6).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [periodEndRaw]);

  const nextRows = [
    { label: "Users retain current access until", value: accessUntil },
    { label: "Facility removed on", value: removedOn },
    { label: "Data archived until", value: archivedUntil },
  ] as const;

  return (
    <Dialog
      open={open}
      onClose={onBackToBilling}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          fontFamily: '"Rethink Sans", sans-serif',
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            backgroundColor: "#f3fdff",
            px: { xs: 2.5, sm: 3 },
            py: 3,
            textAlign: "center",
            borderBottom: "1px solid #d8eff4",
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              backgroundColor: "#dff6dd",
              border: "1px solid #107c10",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 28, color: "#107c10" }} />
          </Box>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: "#107c10",
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            Subscription cancelled
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#525252",
              lineHeight: 1.5,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            Your cancellation is confirmed. All users of {facilityName} will
            retain their current access until {accessUntil}. A confirmation
            email has been sent to all facility admins.
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#262626",
              mb: 1.5,
            }}
          >
            What happens next
          </Typography>

          <Box
            sx={{
              border: "1px solid #d8eff4",
              borderRadius: "8px",
              overflow: "hidden",
              mb: 2,
            }}
          >
            {nextRows.map((row, index) => (
              <Box
                key={row.label}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                  gap: { xs: 0.25, sm: 2 },
                  px: 2,
                  py: 1.25,
                  backgroundColor: index % 2 === 0 ? "#f8fdfe" : "#ffffff",
                  borderBottom: "1px solid #e5f5f8",
                }}
              >
                <Typography sx={{ fontSize: "13px", color: "#737373" }}>
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#262626",
                    textAlign: { xs: "left", sm: "right" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                gap: { xs: 0.25, sm: 2 },
                px: 2,
                py: 1.25,
                backgroundColor: "#ffffff",
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#737373" }}>
                Changed your mind?
              </Typography>
              <Link
                component="button"
                type="button"
                onClick={onResubscribe}
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: "#0a829d",
                  textAlign: { xs: "left", sm: "right" },
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Resubscribe anytime
              </Link>
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Button
              type="button"
              variant="outlined"
              onClick={onBackToBilling}
              startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                fontFamily: '"Rethink Sans", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: "#404040",
                borderColor: "#b3b3b3",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                px: 3,
                py: 1.1,
                "&:hover": {
                  borderColor: "#8a8a8a",
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Back to billing
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionCancelledDialog;
